import {Component, DestroyRef, EventEmitter, inject, Output} from '@angular/core';
import {ControlContainer, FormGroup, FormGroupDirective, ReactiveFormsModule, Validators} from '@angular/forms';

import {TranslateModule, TranslatePipe} from '@ngx-translate/core';
import {PackageSelectComponent} from './package-select/package-select.component';
import {BillingPackage} from './package-select/package-select-item/package-select-item.component';
import {BillingModelPackage} from '../../../models/billing/BillingModelPackage';
import {ClientCreationService} from "../services/client-creation.service";
import {Observable, shareReplay} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DomainService} from '../../../service/domain.service';
import {map} from 'rxjs/operators';
import { AsyncPipe, CommonModule } from '@angular/common';
import {IbpTooltipDirective} from "../../../ibp-directives/ibp-tooltip/ibp-tooltip.directive";
import {expandCollapse} from "../../../shared/animations";


import {IbpButtonComponent} from '../../../ibp-components/third-party/ibp-prime-ng/ibp-button/ibp-button.component';
import {
  IbpCheckboxComponent
} from '../../../ibp-components/third-party/ibp-prime-ng/ibp-checkbox/ibp-checkbox.component';
import {
  IbpCalendarComponent
} from '../../../ibp-components/third-party/ibp-prime-ng/ibp-calendar/ibp-calendar.component';
import {IbpFormHeaderComponent} from "../../../ibp-components/ibp-form-header/ibp-form-header.component";
import {
  IbpInputNumberComponent
} from "../../../ibp-components/third-party/ibp-prime-ng/ibp-input-number/ibp-input-number.component";

@Component({
  selector: 'ibp-service-info-step',
  templateUrl: './service-info-step.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    PackageSelectComponent,
    AsyncPipe,
    IbpTooltipDirective,
    CommonModule,
    TranslateModule,
    IbpButtonComponent,
    IbpCheckboxComponent,
    IbpCalendarComponent,
    IbpFormHeaderComponent,
    IbpInputNumberComponent,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    }],
  animations: [
    expandCollapse],
})
export class ServiceInfoStepComponent {
  private rootFormGroup: FormGroupDirective = inject(FormGroupDirective);
  private clientCreationService: ClientCreationService = inject(ClientCreationService);
  private domainService: DomainService = inject(DomainService);
  readonly #destroyRef = inject(DestroyRef);

  @Output() prevCallback = new EventEmitter();
  @Output() nextCallback = new EventEmitter();

  public form: FormGroup;
  public payrollBillingPackage: BillingPackage[];
  public accountingBillingPackage: BillingPackage[];
  public earliestPossibleStartingDate: Date;
  public serviceEnabled$: Observable<boolean>;

  constructor() {
    const domains$ = this.domainService.findAllForUser().pipe(shareReplay(1), takeUntilDestroyed(this.#destroyRef));

    this.form = this.rootFormGroup.control;
    this.earliestPossibleStartingDate = this.findEarliestPossibleStartingDate();
    this.serviceEnabled$ = domains$.pipe(map(domains => domains.find(domain => domain.id == this.form.get('companyInfo.domain').value).rma));

    this.prepareBillingPackages();
    this.monitorPayrollPackageState();
  }

  public showPayrollPackages(): boolean {
    return !!this.form.get('serviceInfo.enablePayrollPackage').value;
  }

  public showPayrollOptions(): boolean {
    return !!this.form.get('serviceInfo.payrollPackage').value;
  }

  public isFormValidOrDisabled(): boolean {
    return this.form.get('serviceInfo').disabled || this.form.get('serviceInfo').valid;
  }

  public onNextClick() {
    this.clientCreationService.setServiceInfoData(this.form);
    this.nextCallback.emit()
  }

  private findEarliestPossibleStartingDate(): Date {
    // First possible starting date should be 1st of Jan 5 years ago (details: IBP-2229)
    const yearShift = 5;

    return new Date(new Date().getFullYear()-yearShift, 0, 1);
  }

  private prepareBillingPackages() {
    this.accountingBillingPackage = [
      {
        packageSize: BillingModelPackage.S,
        baseCost: 0,
        additionalCost: 1.8,
      },
      {
        packageSize: BillingModelPackage.M,
        baseCost: 35,
        additionalCost: 1.4,
      },
      {
        packageSize: BillingModelPackage.L,
        baseCost: 290,
        additionalCost: 0.95,
      }]
    this.payrollBillingPackage = [
      {
        packageSize: BillingModelPackage.S,
        baseCost: 15,
        additionalCost: 25,
      },
      {
        packageSize: BillingModelPackage.M,
        baseCost: 30,
        additionalCost: 22,
      },
      {
        packageSize: BillingModelPackage.L,
        baseCost: 150,
        additionalCost: 16,
      }]
  }

  private monitorPayrollPackageState() {
    this.form.get("serviceInfo.enablePayrollPackage").valueChanges.pipe(
      takeUntilDestroyed(this.#destroyRef),
    ).subscribe(payrollServicesEnabled => {
      if (!payrollServicesEnabled) {
        this.resetPayrollPackageSettings();
      }
      this.setupPayrollPackageValidation(payrollServicesEnabled);
    })
  }

  private resetPayrollPackageSettings() {
    this.form.get('serviceInfo.payrollPayDay').setValue(null);
    this.form.get('serviceInfo.payrollPackage').setValue(null);
    this.form.get('serviceInfo.executePayrollPayments').setValue(false);
    this.form.get('serviceInfo.executePayrollPaymentsBeforeBankHolidays').setValue(false);

    this.form.get('serviceInfo.payrollPayDay').markAsUntouched();
    this.form.get('serviceInfo.payrollPackage').markAsUntouched();
  }

  private setupPayrollPackageValidation(payrollServicesEnabled: boolean) {
    const payrollPayDayValidators = !!payrollServicesEnabled ? [Validators.required, Validators.min(1), Validators.max(31)] : [];
    const payrollPackageValidator = !!payrollServicesEnabled ? [Validators.required] : [];

    const payrollPayDayControl = this.form.get('serviceInfo.payrollPayDay');
    const payrollPackageControl = this.form.get('serviceInfo.payrollPackage');

    payrollPayDayControl.setValidators(payrollPayDayValidators);
    payrollPayDayControl.updateValueAndValidity();
    payrollPackageControl.setValidators(payrollPackageValidator);
    payrollPackageControl.updateValueAndValidity();
  }
}
